import React, { useState, useContext, useRef } from "react";
import { NameContext } from "../context/NameContext";
import { useTheme } from "../context/ThemeContext";

const ContactForm = ({ onClose, embedded = false }) => {
  const { name } = useContext(NameContext);
  const { isDark } = useTheme();

  const inputClass = `flex-1 bg-transparent text-sm ${
    isDark
      ? "text-[#d0cbb8] placeholder-[#d0cbb8]/20"
      : "text-[#1a1a1a] placeholder-[#1a1a1a]/30"
  } outline-none border-none ring-0 focus:outline-none focus:ring-0 focus:border-none caret-[#AC8E66] font-mono`;

  const t = {
    titleBar:    isDark ? "bg-[#1a1a1a]" : "bg-[#b8b2a0]",
    cardBg:      isDark ? "bg-[#0F0F0F]" : "bg-[#c8c2b0]",
    promptText:  isDark ? "text-[#d0cbb8]/60" : "text-[#1a1a1a]/60",
    labelText:   isDark ? "text-[#d0cbb8]/80" : "text-[#1a1a1a]/80",
    hintText:    isDark ? "text-[#d0cbb8]/30" : "text-[#1a1a1a]/30",
    checkboxBg:  isDark ? "bg-[#d0cbb8]/10" : "bg-[#1a1a1a]/10",
    checkboxKnob: isDark ? "bg-[#d0cbb8]/40" : "bg-[#1a1a1a]/40",
    submitText:  isDark ? "text-[#d0cbb8]/70" : "text-[#1a1a1a]/70",
    titleLabel:     isDark ? "text-[#AC8E66]" : "text-[#1a1a1a]/60",
    terminalBorder: isDark ? "border-[#AC8E66]/30" : "border-[#1a1a1a]/50",
    errorText:   isDark ? "text-red-500" : "text-red-500",
    errorMark:   isDark ? "text-red-500" : "text-red-500",
  };

  const [form, setForm] = useState({
    email: "",
    name: "",
    telefon: "",
    betreff: "",
    message: "",
    datenschutz: false,
  });

  const [status, setStatus] = useState(null);
  const [touched, setTouched] = useState({ email: false, telefon: false });
  const [nameFocused, setNameFocused] = useState(false);
  const nameRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const telefonDigits = form.telefon.replace(/\D/g, "");
  const isTelefonValid =
    /^[0-9+\-()\s]*$/.test(form.telefon.trim()) && telefonDigits.length >= 6;
  const showEmailError = touched.email && form.email.trim() !== "" && !isEmailValid;
  const showTelefonError =
    touched.telefon && form.telefon.trim() !== "" && !isTelefonValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched((prev) => ({ ...prev, email: true, telefon: true }));
    if (!isEmailValid || !isTelefonValid) {
      setStatus("error");
      return;
    }
    setStatus("loading");

    try {
      const res = await fetch("https://www.denisbitter.de/stage01/api/sendContact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error(`Server returned status ${res.status}`);
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Antwort ist kein gültiges JSON!");
      }
      setStatus(data.success ? "success" : "error");
    } catch (err) {
      setStatus("error");
      console.error("Formular Fehler:", err);
    }
  };

  const handleReset = () => {
    setStatus(null);
    setForm({ email: "", name: "", telefon: "", betreff: "", message: "", datenschutz: false });
  };

  const isFormComplete =
    form.email.trim() &&
    form.name.trim() &&
    form.telefon.trim() &&
    form.betreff.trim() &&
    form.message.trim() &&
    form.datenschutz &&
    isEmailValid &&
    isTelefonValid;

  // Erfolg — Terminal-Output
  if (status === "success") {
    if (embedded) {
      return (
        <div className="font-mono">
          <div className="flex items-center gap-2 py-2 mb-3">
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 transition"
                aria-label="Kontaktformular schliessen"
                title="Schliessen"
              />
            ) : (
              <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            )}
            <span className={`ml-3 text-xs ${t.titleLabel} select-none`}>contact</span>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-[#d0cbb8]/50">
              <span className="text-[#AC8E66]">$</span> send --message
            </p>
            <p className="text-sm text-green-400">
              <span className="text-green-400/60">&gt;</span> nachricht gesendet.
            </p>
            <p className="text-sm text-[#d0cbb8]/60 mt-1">
              <span className="text-[#AC8E66]/80">&gt;</span> danke{form.name ? `, ${form.name}` : ""}. denis meldet sich innerhalb eines tages.
            </p>
            <p className="text-sm text-[#d0cbb8]/40 mt-1">
              <span className="text-[#AC8E66]/80">&gt;</span> oder schneller:{" "}
              <a href="tel:+4915153231791" className="text-[#AC8E66] hover:underline">0151 5323 1791</a>
            </p>
            <button
              onClick={handleReset}
              className="mt-4 w-full py-2 text-xs text-[#d0cbb8]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer"
            >
              <span className="text-[#AC8E66]">$</span> new --message
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={`rounded-lg border ${t.terminalBorder} overflow-hidden shadow-xl shadow-[#1a1a1a]/20 font-mono`}>
        <div className={`flex items-center gap-2 px-4 py-2.5 ${t.titleBar} border-b border-[#AC8E66]/10`}>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 transition"
              aria-label="Kontaktformular schliessen"
              title="Schliessen"
            />
          ) : (
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          )}
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className={`ml-3 text-xs ${t.titleLabel} select-none`}>contact</span>
        </div>
        <div className={`px-4 sm:px-6 py-6 ${t.cardBg} space-y-2`}>
          <p className={`text-sm ${t.promptText}`}>
            <span className="text-[#AC8E66]">$</span> send --message
          </p>
          <p className="text-sm text-green-400">
            <span className="text-green-400/60">&gt;</span> nachricht gesendet.
          </p>
          <p className="text-sm text-[#d0cbb8]/60 mt-1">
            <span className="text-[#AC8E66]/80">&gt;</span> danke{form.name ? `, ${form.name}` : ""}. denis meldet sich innerhalb eines tages.
          </p>
          <p className="text-sm text-[#d0cbb8]/40 mt-1">
            <span className="text-[#AC8E66]/80">&gt;</span> oder schneller:{" "}
            <a href="tel:+4915153231791" className="text-[#AC8E66] hover:underline">0151 5323 1791</a>
          </p>
          <button
            onClick={handleReset}
            className={`mt-4 w-full py-2 text-xs ${t.submitText} bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer`}
          >
            <span className="text-[#AC8E66]">$</span> new --message
          </button>
        </div>
      </div>
    );
  }

  if (embedded) {
    return (
      <div className="font-mono">
        <div className="flex items-center gap-2 py-2 mb-3">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 transition"
              aria-label="Kontaktformular schliessen"
              title="Schliessen"
            />
          ) : (
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          )}
          <span className={`ml-3 text-xs ${t.titleLabel} select-none`}>contact</span>
        </div>

        <form onSubmit={handleSubmit} className="py-1">
          <p className={`text-sm ${t.promptText} mb-4`}>
            <span className="text-[#AC8E66]">$</span> contact --init {name && <span className="text-[#AC8E66]">--user {name}</span>}
          </p>

          <div
            className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10 cursor-text"
            onClick={() => nameRef.current?.focus()}
          >
            <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
            <span className={`text-xs ${t.labelText} shrink-0 w-16`}>name</span>
            <div className="flex-1 relative">
              <input
                ref={nameRef}
                type="text"
                name="name"
                placeholder={name || "dein name"}
                value={form.name}
                onChange={handleChange}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                className={inputClass}
              />
              {!form.name && !nameFocused && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 inline-block w-[2px] h-4 bg-[#AC8E66] animate-cursor-blink pointer-events-none " />
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
            <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
            <span className={`text-xs ${t.labelText} shrink-0 w-16`}>email</span>
            <input
              type="email"
              name="email"
              placeholder="deine@email.de"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass}
            />
          </div>
          {showEmailError && (
            <p className={`text-xs ${t.errorText} ml-6 mt-1`}>
              <span className={`${t.errorMark}`}>&gt;</span> error: ungueltige email
            </p>
          )}

          <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
            <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
            <span className={`text-xs ${t.labelText} shrink-0 w-16`}>telefon</span>
            <input
              type="text"
              name="telefon"
              placeholder="0151 ..."
              value={form.telefon}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass}
            />
          </div>
          {showTelefonError && (
            <p className={`text-xs ${t.errorText} ml-6 mt-1`}>
              <span className={`${t.errorMark}`}>&gt;</span> error: ungueltige nummer
            </p>
          )}

          <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
            <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
            <span className={`text-xs ${t.labelText} shrink-0 w-16`}>betreff</span>
            <input
              type="text"
              name="betreff"
              placeholder="um was geht es?"
              value={form.betreff}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          <div className="flex gap-2 py-2 border-b border-[#AC8E66]/10">
            <span className="text-[#AC8E66]/80 text-xs shrink-0 pt-1">&gt;</span>
            <span className={`text-xs ${t.labelText} shrink-0 w-16 pt-1`}>message</span>
            <textarea
              name="message"
              placeholder="alle details hier..."
              rows="4"
              value={form.message}
              onChange={handleChange}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex items-center gap-2 py-3">
            <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
            <span id="contact-form-consent-label" className={`text-xs ${t.labelText} shrink-0`}>datenschutz</span>
            <label htmlFor="contact-form-consent" className="inline-flex items-center cursor-pointer ml-auto">
              <input
                id="contact-form-consent"
                type="checkbox"
                name="datenschutz"
                checked={form.datenschutz}
                onChange={handleChange}
                className="sr-only"
                aria-labelledby="contact-form-consent-label"
              />
              <div className={`w-8 h-4 rounded-full relative transition ${form.datenschutz ? "bg-green-500/30" : t.checkboxBg}`}>
                <div className={`w-3 h-3 rounded-full absolute top-0.5 left-0.5 transition-transform ${form.datenschutz ? "translate-x-4 bg-green-400" : t.checkboxKnob}`} />
              </div>
            </label>
          </div>
          <p className={`text-xs ${t.hintText} ml-6 -mt-1 mb-3`}>
            datenverarbeitung gemaess{" "}
            <a href="/datenschutz" className="underline text-[#AC8E66]/50 hover:text-[#AC8E66]" target="_blank" rel="noopener noreferrer">
              datenschutz
            </a>
          </p>

          {status === "error" && (
            <p className={`text-xs ${t.errorText} mb-3`}>
              <span className={`${t.errorMark}`}>&gt;</span> error: bitte alle felder korrekt ausfuellen.
            </p>
          )}

          <button
            type="submit"
            disabled={!isFormComplete}
            className={`w-full py-2.5 text-xs ${t.submitText} bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer ${
              !isFormComplete ? "opacity-30 cursor-not-allowed" : ""
            } ${status === "loading" ? "animate-pulse" : ""}`}
          >
            <span className="text-[#AC8E66]">$</span> send --message
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${t.terminalBorder} overflow-hidden shadow-xl shadow-[#1a1a1a]/20 font-mono`}>

      {/* Title Bar */}
      <div className={`flex items-center gap-2 px-4 py-2.5 ${t.titleBar} border-b border-[#AC8E66]/10`}>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 transition"
            aria-label="Kontaktformular schliessen"
            title="Schliessen"
          />
        ) : (
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        )}
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <span className={`ml-3 text-xs ${t.titleLabel} select-none`}>contact</span>
      </div>

      {/* Terminal Content */}
      <form onSubmit={handleSubmit} className={`px-4 sm:px-6 py-5 ${t.cardBg}`}>

        <p className={`text-sm ${t.promptText} mb-4`}>
          <span className="text-[#AC8E66]">$</span> contact --init {name && <span className="text-[#AC8E66]">--user {name}</span>}
        </p>

        {/* Name */}
        <div
          className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10 cursor-text"
          onClick={() => nameRef.current?.focus()}
        >
          <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
          <span className={`text-xs ${t.labelText} shrink-0 w-16`}>name</span>
          <div className="flex-1 relative">
            <input
              ref={nameRef}
              type="text"
              name="name"
              placeholder={name || "dein name"}
              value={form.name}
              onChange={handleChange}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              className={inputClass}
            />
            {!form.name && !nameFocused && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 inline-block w-[2px] h-4 bg-[#AC8E66] animate-cursor-blink pointer-events-none" />
            )}
          </div>
        </div>

        {/* Email */}
        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
          <span className={`text-xs ${t.labelText} shrink-0 w-16`}>email</span>
          <input
            type="email"
            name="email"
            placeholder="deine@email.de"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={inputClass}
          />
        </div>
        {showEmailError && (
          <p className={`text-xs ${t.errorText} ml-6 mt-1`}>
            <span className={`${t.errorMark}`}>&gt;</span> error: ungueltige email
          </p>
        )}

        {/* Telefon */}
        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
          <span className={`text-xs ${t.labelText} shrink-0 w-16`}>telefon</span>
          <input
            type="text"
            name="telefon"
            placeholder="0151 ..."
            value={form.telefon}
            onChange={handleChange}
            onBlur={handleBlur}
            className={inputClass}
          />
        </div>
        {showTelefonError && (
          <p className={`text-xs ${t.errorText} ml-6 mt-1`}>
            <span className={`${t.errorMark}`}>&gt;</span> error: ungueltige nummer
          </p>
        )}

        {/* Betreff */}
        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
          <span className={`text-xs ${t.labelText} shrink-0 w-16`}>betreff</span>
          <input
            type="text"
            name="betreff"
            placeholder="um was geht es?"
            value={form.betreff}
            onChange={handleChange}
            className={inputClass}
          />
        </div>

        {/* Nachricht */}
        <div className="flex gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs shrink-0 pt-1">&gt;</span>
          <span className={`text-xs ${t.labelText} shrink-0 w-16 pt-1`}>message</span>
          <textarea
            name="message"
            placeholder="alle details hier..."
            rows="4"
            value={form.message}
            onChange={handleChange}
            className={`${inputClass} resize-none`}
          />
        </div>

        {/* Datenschutz */}
        <div className="flex items-center gap-2 py-3">
          <span className="text-[#AC8E66]/80 text-xs shrink-0">&gt;</span>
          <span id="contact-form-consent-inline-label" className={`text-xs ${t.labelText} shrink-0`}>datenschutz</span>
          <label htmlFor="contact-form-consent-inline" className="inline-flex items-center cursor-pointer ml-auto">
            <input
              id="contact-form-consent-inline"
              type="checkbox"
              name="datenschutz"
              checked={form.datenschutz}
              onChange={handleChange}
              className="sr-only"
              aria-labelledby="contact-form-consent-inline-label"
            />
            <div className={`w-8 h-4 rounded-full relative transition ${form.datenschutz ? "bg-green-500/30" : t.checkboxBg}`}>
              <div className={`w-3 h-3 rounded-full absolute top-0.5 left-0.5 transition-transform ${form.datenschutz ? "translate-x-4 bg-green-400" : t.checkboxKnob}`} />
            </div>
          </label>
        </div>
        <p className={`text-xs ${t.hintText} ml-6 -mt-1 mb-3`}>
          datenverarbeitung gemaess{" "}
          <a href="/datenschutz" className="underline text-[#AC8E66]/50 hover:text-[#AC8E66]" target="_blank" rel="noopener noreferrer">
            datenschutz
          </a>
        </p>

        {/* Error */}
        {status === "error" && (
          <p className={`text-xs ${t.errorText} mb-3`}>
            <span className={`${t.errorMark}`}>&gt;</span> error: bitte alle felder korrekt ausfuellen.
          </p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={!isFormComplete}
          className={`w-full py-2.5 text-xs ${t.submitText} bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer ${
            !isFormComplete ? "opacity-30 cursor-not-allowed" : ""
          } ${status === "loading" ? "animate-pulse" : ""}`}
        >
          <span className="text-[#AC8E66]">$</span> send --message
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
